import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import InlineNameEditor from '../ui/InlineNameEditor';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import LayoutThumbnail from './LayoutThumbnail';
import TermIconButton from './TermIconButton';
import { deleteLayoutWithUndo } from './layoutActions';

const slotColors = (layout, profiles) =>
  Object.fromEntries(
    (layout.slots || [])
      .map((s, i) => [i, profiles.find((p) => p.id === s.profileId)?.icon?.color])
      .filter(([, c]) => c)
  );

/**
 * One saved layout in the Layouts ▾ menu: thumbnail, name (or the inline
 * rename editor while `renaming` is this layout's id), rename + delete.
 * Click / Enter calls `onOpen(layout)` unless a rename is in progress.
 */
export default function SavedLayoutRow({ layout: l, profiles, renaming, setRenaming, onOpen }) {
  const { t } = useTranslation();
  return (
    <div
      className="term-menu-row term-layout-row"
      role="menuitem"
      tabIndex={0}
      onClick={() => {
        if (renaming) return;
        onOpen(l);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !renaming) {
          onOpen(l);
        }
      }}
    >
      <LayoutThumbnail tree={l.tree} saved colors={slotColors(l, profiles)} />
      {renaming === l.id ? (
        <span className="term-menu-row-name" onClick={(e) => e.stopPropagation()}>
          <InlineNameEditor
            value={l.name}
            onSave={(v) => {
              useTerminalConfigStore.getState().renameLayout(l.id, v);
              setRenaming(null);
            }}
            onCancel={() => setRenaming(null)}
          />
        </span>
      ) : (
        <span className="term-menu-row-name">
          {l.name}
          <span className="term-layout-meta">{t('terminalExt.paneCount', '{{count}} panes', { count: (l.slots || []).length })}</span>
        </span>
      )}
      <span className="term-menu-row-actions">
        <TermIconButton className="term-menu-mini" label={t('terminalExt.renameLayout', 'Rename layout')} onClick={() => setRenaming(l.id)}>
          <Pencil size={12} />
        </TermIconButton>
        <TermIconButton className="term-menu-mini" label={t('terminalExt.deleteLayout', 'Delete layout')} onClick={() => deleteLayoutWithUndo(l)}>
          <Trash2 size={12} />
        </TermIconButton>
      </span>
    </div>
  );
}
